import { Typography, Grid, Box, Alert } from "@mui/material";
import { FormattedMessage as T } from "react-intl";
import { standardDateFormat } from "utils/dateUtils";
import { ISkuQualificationRequest, ValidateResponse } from "services/KnowfullyAdminClient";
import { format } from "date-fns";

const getStyles = () => ({
  gridTitle: {
    fontWeight: 700,
    marginBottom: 1,
  },
  grid: {
    borderTop: 1,
    borderColor: "divider",
    mt: 1,
  },
  courseTitle: {
    marginBottom: 2,
  },
  warningAlert: {
    marginTop: 2,
  },
});

function formatDate(date: Date | undefined) {
  return date ? format(new Date(date), "yyyy-MM-dd") : "";
}

function hasMismatch(
  response: ValidateResponse,
  skuQualificationRequest: ISkuQualificationRequest | null
) {
  const nabpRelease = formatDate(response.releaseDate);
  const nabpExpire = formatDate(response.expirationDate);
  const skuRelease = formatDate(skuQualificationRequest?.releaseDate);
  const skuExpire = formatDate(
    skuQualificationRequest?.creditSubmissionExpirationDate
  );
  const creditsMatch =
    String(response.contactHours ?? "") ===
    String(skuQualificationRequest?.credits ?? "");
  const releaseMatch = nabpRelease === skuRelease;
  const expireMatch = nabpExpire === skuExpire;
  return !creditsMatch || !releaseMatch || !expireMatch;
}

export type AcpeValidationGridProps = {
  response: ValidateResponse;
  skuQualificationRequest: ISkuQualificationRequest | null;
};

const AcpeValidationGrid = ({ response, skuQualificationRequest }: AcpeValidationGridProps) => {
  const styles = getStyles();
  const DateCell = ({ labelKey, date }: { labelKey: string; date?: Date }) => (
    <div>
      <T id={labelKey} />: {date ? format(new Date(date), standardDateFormat) : "-"}
    </div>
  );

  return (
    <Box>
      <Typography sx={styles.courseTitle}>
        <T id="courseTitle" />: {response.title}
      </Typography>
      <Grid container spacing={2} sx={styles.grid}>
        <Grid item xs={12} md={6}>
          <Typography sx={styles.gridTitle}>
            <T id="nabp" />
          </Typography>
          <DateCell labelKey="skuFormReleaseDateLabel" date={response.releaseDate} />
          <DateCell labelKey="creditExpirationDate" date={response.expirationDate} />
          <div>
            <T id="credits" />: {response.contactHours ?? "-"}
          </div>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography sx={styles.gridTitle}>
            <T id="currentSkuQualification" />
          </Typography>
          <DateCell labelKey="skuFormReleaseDateLabel" date={skuQualificationRequest?.releaseDate} />
          <DateCell labelKey="creditExpirationDate" date={skuQualificationRequest?.creditSubmissionExpirationDate} />
          <div>
            <T id="credits" />: {skuQualificationRequest?.credits ?? "-"}
          </div>
        </Grid>
      </Grid>
      {hasMismatch(response, skuQualificationRequest) && (
        <Alert severity="warning" sx={styles.warningAlert}>
          <T id="nabpMismatchWarning" />
        </Alert>
      )}
    </Box>
  );
};

export default AcpeValidationGrid;
